import {
  Button,
  Checkbox,
  cn,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  RadioCardGroup,
  RadioCardItem,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { EmptySaveTooltip } from './EmptySaveTooltip';
import { BlockedEditTooltip } from '@/features/jobs/job-shared/BlockedEditTooltip';
import { maskBrCurrencyInput } from './currency';
import { LockIcon, PlusIcon } from './icons';
import { JobHeaderLogo } from '@/features/jobs/job-shared/JobHeaderLogo';

export type SalaryDisplay = 'valor-exato' | 'faixa-salarial' | 'nao-divulgar';

export interface JobEditCompensationValues {
  salaryDisplay: string;
  salary: string;
  salaryMin: string;
  salaryMax: string;
  selectedBenefits: string[];
  selectedAdditionalPayments: string[];
}

export interface JobEditCompensationPageProps {
  jobName: string;
  companyLogoUrl?: string | null;
  companyName?: string | null;
  values: JobEditCompensationValues;
  errors: Record<string, string>;
  isSubmitting: boolean;
  /**
   * `false` quando há erro ativo (campo obrigatório vazio OU faixa
   * `max <= min`). O botão "Salvar alterações" fica `disabled` —
   * paridade com V10/EU-163 (fix do bug QA Juliana em que o submit
   * disparava parcial mesmo com a faixa inválida).
   */
  canSubmit: boolean;
  /**
   * EU-709 RN-14 — a seção inteira é opcional neste tipo de vaga (a API diz).
   * Muda as dicas dos campos e liga o gate de "há algo a salvar".
   */
  sectionOptional: boolean;
  /** EU-709 RN-21 — existe ao menos um campo preenchido para gravar. */
  hasSomethingToSave: boolean;
  /** EU-569 — vaga Encerrada / Cancelada: modo somente visualização. */
  readOnly?: boolean;
  benefitOptions: readonly string[];
  lockedBenefits: readonly string[];
  additionalPaymentOptions: readonly string[];
  lockedAdditionalPayments: readonly string[];
  onFieldChange: <K extends keyof JobEditCompensationValues>(
    field: K,
    value: JobEditCompensationValues[K]
  ) => void;
  onToggleBenefit: (value: string) => void;
  onToggleAdditionalPayment: (value: string) => void;
  onAddBenefit: () => void;
  onAddAdditionalPayment: () => void;
  onBack: () => void;
  onSubmit: () => void;
}

const SALARY_DISPLAY_OPTIONS: readonly { value: SalaryDisplay; label: string }[] = [
  { value: 'valor-exato', label: 'Valor exato' },
  { value: 'faixa-salarial', label: 'Faixa salarial' },
  { value: 'nao-divulgar', label: 'Não divulgar' },
];

/** EU-709 RN-14 — a frase do Figma para campo que deixou de ser exigido. */
const OPTIONAL_FIELD_HINT = 'Esse campo é opcional';

export function JobEditCompensationPage({
  jobName,
  companyLogoUrl,
  companyName,
  values,
  errors,
  isSubmitting,
  canSubmit,
  sectionOptional,
  hasSomethingToSave,
  readOnly = false,
  benefitOptions,
  lockedBenefits,
  additionalPaymentOptions,
  lockedAdditionalPayments,
  onFieldChange,
  onToggleBenefit,
  onToggleAdditionalPayment,
  onAddBenefit,
  onAddAdditionalPayment,
  onBack,
  onSubmit,
}: JobEditCompensationPageProps) {
  const showExactSalary = values.salaryDisplay === 'valor-exato';
  const showSalaryRange = values.salaryDisplay === 'faixa-salarial';
  // RN-21 — numa vaga Banco de talentos o bloqueio do `Salvar` nunca é por
  // campo obrigatório (não existe nenhum aqui): é por não haver o que gravar.
  const saveBlockedByEmptiness = sectionOptional && !hasSomethingToSave;

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="jobs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-8 pb-16 md:px-8">
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <header className="flex w-full max-w-[630px] flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <JobHeaderLogo logoUrl={companyLogoUrl} companyName={companyName} />
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                {jobName}
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Remuneração
            </p>
          </header>

          <div className="flex w-full max-w-[630px] flex-1 flex-col gap-5">
            {/* EU-569 — fieldset nativo desabilita os controles em modo
                visualização (radios, inputs de moeda, chips e "Adicionar novo"). */}
            <fieldset
              disabled={readOnly}
              className="contents"
              data-testid={readOnly ? 'job-edit-readonly-fieldset' : undefined}
            >
              <BlockedEditTooltip enabled={readOnly}>
                <FormField error={Boolean(errors.salaryDisplay)}>
                  <FormFieldLabel className="text-foreground">
                    Tipo de divulgação do salário
                  </FormFieldLabel>
                  <RadioCardGroup
                    value={values.salaryDisplay}
                    onValueChange={(value) => onFieldChange('salaryDisplay', value)}
                    className="flex-wrap"
                  >
                    {SALARY_DISPLAY_OPTIONS.map((option) => (
                      <RadioCardItem
                        key={option.value}
                        value={option.value}
                        className="group px-4 py-4"
                        data-testid={`job-compensation-salary-display-${option.value}`}
                      >
                        {option.label}
                      </RadioCardItem>
                    ))}
                  </RadioCardGroup>
                  {sectionOptional ? <FormFieldHint>{OPTIONAL_FIELD_HINT}</FormFieldHint> : null}
                  <FormFieldError className="text-sm leading-5 text-muted-foreground">
                    {errors.salaryDisplay}
                  </FormFieldError>
                </FormField>
              </BlockedEditTooltip>

              {showExactSalary ? (
                <BlockedEditTooltip enabled={readOnly}>
                  <FormField error={Boolean(errors.salary)}>
                    <FormFieldLabel className="text-foreground">Salário</FormFieldLabel>
                    <CurrencyInput
                      value={values.salary}
                      placeholder="Ex.: 5.000,00"
                      hasError={Boolean(errors.salary)}
                      onChange={(value) => onFieldChange('salary', value)}
                      data-testid="job-compensation-salary"
                    />
                    <FormFieldError className="text-sm leading-5 text-muted-foreground">
                      {errors.salary}
                    </FormFieldError>
                  </FormField>
                </BlockedEditTooltip>
              ) : null}

              {showSalaryRange ? (
                <BlockedEditTooltip enabled={readOnly}>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <FormField error={Boolean(errors.salaryMin)}>
                      <FormFieldLabel className="text-foreground">Valor mínimo</FormFieldLabel>
                      <CurrencyInput
                        value={values.salaryMin}
                        placeholder="Ex.: R$ 3.000,00"
                        hasError={Boolean(errors.salaryMin)}
                        onChange={(value) => onFieldChange('salaryMin', value)}
                        data-testid="job-compensation-salary-min"
                      />
                      <FormFieldError className="text-sm leading-5 text-muted-foreground">
                        {errors.salaryMin}
                      </FormFieldError>
                    </FormField>
                    <FormField error={Boolean(errors.salaryMax)}>
                      <FormFieldLabel className="text-foreground">Valor máximo</FormFieldLabel>
                      <CurrencyInput
                        value={values.salaryMax}
                        placeholder="Ex.: R$ 5.000,00"
                        hasError={Boolean(errors.salaryMax)}
                        onChange={(value) => onFieldChange('salaryMax', value)}
                        data-testid="job-compensation-salary-max"
                      />
                      <FormFieldError className="text-sm leading-5 text-muted-foreground">
                        {errors.salaryMax}
                      </FormFieldError>
                    </FormField>
                  </div>
                </BlockedEditTooltip>
              ) : null}

              <BlockedEditTooltip enabled={readOnly}>
                <FormField>
                  <FormFieldLabel className="text-foreground">Benefícios</FormFieldLabel>
                  <CheckboxPillGroup
                    options={benefitOptions}
                    selected={values.selectedBenefits}
                    locked={lockedBenefits}
                    onToggle={onToggleBenefit}
                    onAddNew={onAddBenefit}
                    testIdPrefix="job-compensation-benefit"
                  />
                  <FormFieldHint>
                    {sectionOptional
                      ? OPTIONAL_FIELD_HINT
                      : 'Os benefícios do programa foram aplicados automaticamente.'}
                  </FormFieldHint>
                </FormField>
              </BlockedEditTooltip>

              <BlockedEditTooltip enabled={readOnly}>
                <FormField>
                  <FormFieldLabel className="text-foreground">Pagamento adicionais</FormFieldLabel>
                  <CheckboxPillGroup
                    options={additionalPaymentOptions}
                    selected={values.selectedAdditionalPayments}
                    locked={lockedAdditionalPayments}
                    onToggle={onToggleAdditionalPayment}
                    onAddNew={onAddAdditionalPayment}
                    testIdPrefix="job-compensation-additional-payment"
                  />
                  <FormFieldHint>
                    {sectionOptional
                      ? OPTIONAL_FIELD_HINT
                      : 'Os pagamento adicionais do programa foram aplicados automaticamente.'}
                  </FormFieldHint>
                </FormField>
              </BlockedEditTooltip>
            </fieldset>

            <div className="mt-auto flex flex-row justify-between gap-2 pt-8">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onBack}
                data-testid="job-compensation-back"
              >
                Voltar
              </Button>
              {readOnly ? (
                <Button type="button" size="lg" disabled data-testid="job-edit-save-blocked">
                  Salvar alterações
                </Button>
              ) : (
                <EmptySaveTooltip enabled={saveBlockedByEmptiness}>
                  <Button
                    type="submit"
                    size="lg"
                    loading={isSubmitting}
                    disabled={!canSubmit || isSubmitting || saveBlockedByEmptiness}
                    data-testid="job-compensation-submit"
                  >
                    Salvar alterações
                  </Button>
                </EmptySaveTooltip>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

interface CurrencyInputProps {
  value: string;
  placeholder: string;
  hasError: boolean;
  onChange: (value: string) => void;
  'data-testid'?: string;
}

function CurrencyInput({
  value,
  placeholder,
  hasError,
  onChange,
  'data-testid': testId,
}: CurrencyInputProps) {
  return (
    <div
      className={cn(
        'flex h-9 w-full flex-row items-center gap-2 overflow-hidden rounded-md border bg-background px-3 text-sm shadow-2xs transition-colors',
        hasError ? 'border-destructive' : 'border-input',
        'focus-within:outline focus-within:outline-2 focus-within:-outline-offset-1 focus-within:outline-primary'
      )}
    >
      <span className="shrink-0 text-sm font-medium leading-5 text-muted-foreground">R$</span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-sm leading-5 text-foreground placeholder:text-muted-foreground focus:outline-none"
        // BRL mask runs on every keystroke: digits pile up from the right
        // as cents and the displayed string is always re-formatted with
        // thousand-separators + comma decimal. Numeric inputMode brings
        // the right keypad on mobile.
        onChange={(event) => onChange(maskBrCurrencyInput(event.target.value))}
        data-testid={testId}
      />
    </div>
  );
}

interface CheckboxPillGroupProps {
  options: readonly string[];
  selected: string[];
  locked: readonly string[];
  onToggle: (value: string) => void;
  onAddNew: () => void;
  testIdPrefix: string;
}

function CheckboxPillGroup({
  options,
  selected,
  locked,
  onToggle,
  onAddNew,
  testIdPrefix,
}: CheckboxPillGroupProps) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {options.map((option) => {
        const isChecked = selected.includes(option);
        const isLocked = locked.includes(option);
        return (
          <CheckboxPill
            key={option}
            label={option}
            checked={isChecked}
            locked={isLocked}
            onCheckedChange={() => onToggle(option)}
            data-testid={`${testIdPrefix}-${slugify(option)}`}
          />
        );
      })}
      <Button
        type="button"
        variant="secondary"
        size="lg"
        onClick={onAddNew}
        className="self-start"
        data-testid={`${testIdPrefix}-add`}
      >
        Adicionar novo
        <PlusIcon className="size-4" />
      </Button>
    </div>
  );
}

interface CheckboxPillProps {
  label: string;
  checked: boolean;
  locked: boolean;
  onCheckedChange: () => void;
  'data-testid'?: string;
}

function CheckboxPill({
  label,
  checked,
  locked,
  onCheckedChange,
  'data-testid': testId,
}: CheckboxPillProps) {
  return (
    <label
      className={cn(
        'group inline-flex select-none items-center gap-2 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
        locked
          ? 'cursor-not-allowed border-border text-muted-foreground'
          : checked
            ? 'cursor-pointer border-primary bg-primary/10 text-foreground'
            : 'cursor-pointer border-border text-foreground hover:border-primary'
      )}
    >
      {locked ? (
        <LockIcon className="size-4 text-muted-foreground" />
      ) : (
        <Checkbox
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="size-4"
          data-testid={testId}
        />
      )}
      <span>{label}</span>
    </label>
  );
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
